import React, { useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { ToastProvider } from "@/components/ToastProvider";
import ErrorBoundary from "./components/ErrorBoundary";
import NavigationBar from "./components/NavigationBar";
import Footer from "./components/Footer";
import { trackPageView } from "@/firebase/firebase";

// Eagerly import all pages for full pre-rendering and SEO
import Home from "./pages/Home";
import Quote from "./pages/Quote";
import PurchaseLeads from "./pages/PurchaseLeads";
import Faq from "./pages/Faq";
import Calculator from "./pages/Calculator";
import AboutJesseGarlick from "./pages/AboutJesseGarlick";
import ContactPage from "./pages/ContactPage";
import Login from "./pages/Login";
import Careers from "./pages/Careers";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Blog from "./modules/blog/Blog";
import BlogPostPage from "./modules/blog/BlogPostPage";
import BlogAdmin from "./modules/blog/BlogAdmin";
import BlogEditor from "./modules/blog/BlogEditor";
import LeadsAdmin from "./pages/admin/LeadsAdmin";

// Analytics wrapper component for tracking page views
const AnalyticsWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname);
  }, [location]);

  return <>{children}</>;
};

function App() {
  return (
    <ToastProvider>
      <AnalyticsWrapper>
        <NavigationBar />

        <Routes>
          {/* Eagerly load pages with individual error boundaries for crawlability */}
          <Route
            path="/"
            element={
              <ErrorBoundary>
                <Home />
              </ErrorBoundary>
            }
          />
          <Route
            path="/quote"
            element={
              <ErrorBoundary>
                <Quote />
              </ErrorBoundary>
            }
          />
          <Route
            path="/purchase-leads"
            element={
              <ErrorBoundary>
                <PurchaseLeads />
              </ErrorBoundary>
            }
          />
          <Route
            path="/faq"
            element={
              <ErrorBoundary>
                <Faq />
              </ErrorBoundary>
            }
          />
          <Route
            path="/calculator"
            element={
              <ErrorBoundary>
                <Calculator />
              </ErrorBoundary>
            }
          />
          <Route
            path="/about"
            element={
              <ErrorBoundary>
                <AboutJesseGarlick />
              </ErrorBoundary>
            }
          />
          <Route
            path="/contact"
            element={
              <ErrorBoundary>
                <ContactPage />
              </ErrorBoundary>
            }
          />
          <Route
            path="/login"
            element={
              <ErrorBoundary>
                <Login />
              </ErrorBoundary>
            }
          />
          <Route
            path="/careers"
            element={
              <ErrorBoundary>
                <Careers />
              </ErrorBoundary>
            }
          />
          <Route
            path="/privacy"
            element={
              <ErrorBoundary>
                <Privacy />
              </ErrorBoundary>
            }
          />
          <Route
            path="/terms"
            element={
              <ErrorBoundary>
                <Terms />
              </ErrorBoundary>
            }
          />

          {/* Blog routes with error boundaries */}
          <Route
            path="/blog"
            element={
              <ErrorBoundary>
                <Blog />
              </ErrorBoundary>
            }
          />
          <Route
            path="/blog/:slug"
            element={
              <ErrorBoundary>
                <BlogPostPage />
              </ErrorBoundary>
            }
          />
          <Route
            path="/admin/blog"
            element={
              <ErrorBoundary>
                <BlogAdmin />
              </ErrorBoundary>
            }
          />
          <Route
            path="/admin/blog/new"
            element={
              <ErrorBoundary>
                <BlogEditor />
              </ErrorBoundary>
            }
          />
          <Route
            path="/admin/blog/edit/:id"
            element={
              <ErrorBoundary>
                <BlogEditor />
              </ErrorBoundary>
            }
          />
          <Route
            path="/admin/leads"
            element={
              <ErrorBoundary>
                <LeadsAdmin />
              </ErrorBoundary>
            }
          />
        </Routes>

        <Footer />
      </AnalyticsWrapper>
    </ToastProvider>
  );
}

export default App;
